/*
 * HomeFixer360 - Admin Coupon Management
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, TrendingUp, Tag, Calendar, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    couponType: 'free_beta',
    description: '',
    discountPercent: '',
    discountFixed: '',
    trialDays: '',
    grantsBetaAccess: true,
    maxUses: '',
    maxUsesPerUser: 1,
    validFrom: '',
    validUntil: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadCoupons();
    loadStats();
  }, []);

  const loadCoupons = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/coupons/`);
      setCoupons(response.data);
    } catch (error) {
      console.error('Error loading coupons:', error);
      showMessage('error', 'Failed to load coupons');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/coupons/stats/usage`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();

    try {
      const couponData = {
        code: newCoupon.code.toUpperCase(),
        coupon_type: newCoupon.couponType,
        description: newCoupon.description || null,
        discount_percent: newCoupon.discountPercent ? parseFloat(newCoupon.discountPercent) : null,
        discount_fixed: newCoupon.discountFixed ? parseFloat(newCoupon.discountFixed) : null,
        trial_days: newCoupon.trialDays ? parseInt(newCoupon.trialDays) : null,
        grants_beta_access: newCoupon.grantsBetaAccess,
        max_uses: newCoupon.maxUses ? parseInt(newCoupon.maxUses) : null,
        max_uses_per_user: parseInt(newCoupon.maxUsesPerUser),
        valid_from: newCoupon.validFrom || null,
        valid_until: newCoupon.validUntil || null
      };

      await axios.post(`${BACKEND_URL}/api/coupons/`, couponData);

      showMessage('success', 'Coupon created successfully');
      setShowCreateModal(false);
      resetForm();
      loadCoupons();
      loadStats();
    } catch (error) {
      console.error('Error creating coupon:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleToggleStatus = async (couponId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(`${BACKEND_URL}/api/coupons/${couponId}/status`, null, {
        params: { new_status: newStatus }
      });
      loadCoupons();
      showMessage('success', `Coupon ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling status:', error);
      showMessage('error', 'Failed to update coupon status');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/coupons/${couponId}`);
      loadCoupons();
      loadStats();
      showMessage('success', 'Coupon deleted successfully');
    } catch (error) {
      console.error('Error deleting coupon:', error);
      showMessage('error', 'Failed to delete coupon');
    }
  };

  const resetForm = () => {
    setNewCoupon({
      code: '',
      couponType: 'free_beta',
      description: '',
      discountPercent: '',
      discountFixed: '',
      trialDays: '',
      grantsBetaAccess: true,
      maxUses: '',
      maxUsesPerUser: 1,
      validFrom: '',
      validUntil: ''
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      depleted: 'bg-orange-100 text-orange-800',
      disabled: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const styles = {
      free_beta: 'bg-blue-100 text-blue-800',
      discount: 'bg-purple-100 text-purple-800',
      extended_trial: 'bg-indigo-100 text-indigo-800',
      combo: 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800'
    };

    const labels = {
      free_beta: 'Free Beta',
      discount: 'Discount',
      extended_trial: 'Extended Trial',
      combo: 'Combo'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[type]}`}>
        {labels[type]}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Coupon Management</h1>
          <p className="text-gray-600 mt-1">Create and manage promotional coupons</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 
          'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Coupons</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_coupons}</p>
              </div>
              <Tag className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Coupons</p>
                <p className="text-2xl font-bold text-green-600">{stats.active_coupons}</p>
              </div>
              <ToggleRight className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Redemptions</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_redemptions}</p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg. Usage</p>
                <p className="text-2xl font-bold text-orange-600">
                  {stats.total_coupons > 0 ? (stats.total_redemptions / stats.total_coupons).toFixed(1) : 0}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Coupons List */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading coupons...</p>
        </div>
      ) : coupons.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Tag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No coupons yet</h3>
          <p className="text-gray-600 mb-4">Create your first coupon to start offering discounts</p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Create First Coupon
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benefits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-mono font-bold text-blue-600">{coupon.code}</div>
                    {coupon.description && (
                      <div className="text-xs text-gray-500">{coupon.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getTypeBadge(coupon.coupon_type)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {coupon.discount_percent && <div>• {coupon.discount_percent}% off</div>}
                    {coupon.discount_fixed && <div>• ${coupon.discount_fixed} off</div>}
                    {coupon.trial_days && <div>• {coupon.trial_days} days trial</div>}
                    {coupon.grants_beta_access && <div>• Beta access</div>}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div>{coupon.current_uses} / {coupon.max_uses || '∞'}</div>
                    <div className="text-xs text-gray-500">Max per user: {coupon.max_uses_per_user}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {formatDate(coupon.valid_until)}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(coupon.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handleToggleStatus(coupon.id, coupon.status)}
                        variant="outline"
                        size="sm"
                        disabled={coupon.status === 'depleted' || coupon.status === 'expired'}
                      >
                        {coupon.status === 'active' ? (
                          <ToggleLeft className="w-4 h-4" />
                        ) : (
                          <ToggleRight className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDelete(coupon.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Coupon Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create New Coupon</h2>

            <form onSubmit={handleCreateCoupon} className="space-y-4">
              {/* Coupon Code */}
              <div>
                <Label htmlFor="code">Coupon Code *</Label>
                <Input
                  id="code"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  placeholder="BETA50"
                  required
                  className="font-mono"
                />
              </div>

              {/* Coupon Type */}
              <div>
                <Label htmlFor="couponType">Coupon Type *</Label>
                <select
                  id="couponType"
                  value={newCoupon.couponType}
                  onChange={(e) => setNewCoupon({ ...newCoupon, couponType: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="free_beta">Free Beta Access</option>
                  <option value="discount">Discount</option>
                  <option value="extended_trial">Extended Trial</option>
                  <option value="combo">Combo (Multiple Benefits)</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newCoupon.description}
                  onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                  placeholder="Early beta tester discount"
                  rows={2}
                />
              </div>

              {/* Discount Settings */}
              {(newCoupon.couponType === 'discount' || newCoupon.couponType === 'combo') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="discountPercent">Discount Percent (%)</Label>
                    <Input
                      id="discountPercent"
                      type="number"
                      min="0"
                      max="100"
                      value={newCoupon.discountPercent}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discountPercent: e.target.value })}
                      placeholder="50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="discountFixed">Discount Fixed ($)</Label>
                    <Input
                      id="discountFixed"
                      type="number"
                      min="0"
                      value={newCoupon.discountFixed}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discountFixed: e.target.value })}
                      placeholder="100"
                    />
                  </div>
                </div>
              )}

              {/* Trial Days */}
              {(newCoupon.couponType === 'extended_trial' || newCoupon.couponType === 'combo') && (
                <div>
                  <Label htmlFor="trialDays">Trial Days</Label>
                  <Input
                    id="trialDays"
                    type="number"
                    min="0"
                    value={newCoupon.trialDays}
                    onChange={(e) => setNewCoupon({ ...newCoupon, trialDays: e.target.value })}
                    placeholder="60"
                  />
                </div>
              )}

              {/* Beta Access */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="grantsBetaAccess"
                  checked={newCoupon.grantsBetaAccess}
                  onChange={(e) => setNewCoupon({ ...newCoupon, grantsBetaAccess: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <Label htmlFor="grantsBetaAccess">Grants Beta Access</Label>
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maxUses">Max Total Uses (blank = unlimited)</Label>
                  <Input
                    id="maxUses"
                    type="number"
                    min="0"
                    value={newCoupon.maxUses}
                    onChange={(e) => setNewCoupon({ ...newCoupon, maxUses: e.target.value })}
                    placeholder="100"
                  />
                </div>
                <div>
                  <Label htmlFor="maxUsesPerUser">Max Uses Per User *</Label>
                  <Input
                    id="maxUsesPerUser"
                    type="number"
                    min="1"
                    value={newCoupon.maxUsesPerUser}
                    onChange={(e) => setNewCoupon({ ...newCoupon, maxUsesPerUser: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Validity Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="validFrom">Valid From</Label>
                  <Input
                    id="validFrom"
                    type="datetime-local"
                    value={newCoupon.validFrom}
                    onChange={(e) => setNewCoupon({ ...newCoupon, validFrom: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="validUntil">Valid Until</Label>
                  <Input
                    id="validUntil"
                    type="datetime-local"
                    value={newCoupon.validUntil}
                    onChange={(e) => setNewCoupon({ ...newCoupon, validUntil: e.target.value })}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Coupon
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
